import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { supportedLanguages, type Language } from '@up2d8/contracts';
import { cn } from '@up2d8/ui';
import { AdminIcon, Avatar } from '../../modules/admin-kit';
import { setLanguage } from '../../shared/i18n';
import { languageLabels } from '../../shared/language-switcher';
import { useSession } from '../../shared/session';
import { useMyTenants, useSwitchTenant } from '../../shared/tenant-switcher';

type View = 'main' | 'language' | 'tenant';

/**
 * De avatarknop rechts in de kopbalk en het avatarmenu (basis-spec A §3, spec 01 T12).
 *
 * Alles over jezelf op één plek: naam en e-mail, Wachtwoord wijzigen, Taal, Ander bedrijf kiezen,
 * Afmelden. Het menu-item "Wachtwoord wijzigen" en de taalkeuze onder in de zijbalk zijn daarmee
 * weg. Taal en bedrijf openen een tweede lijst in hetzelfde menu, met "Terug" bovenaan: een
 * uitklapmenu naast een uitklapmenu valt op 280 breed buiten het scherm.
 *
 * Toetsenbord zoals een menu (WAI-ARIA): pijltjes omhoog en omlaag, Home en End, Escape sluit en
 * zet de focus terug op de knop.
 */
export function AdminAccountMenu({
  zone,
  roleLine,
}: {
  zone: 'admin' | 'platform';
  roleLine: string;
}) {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();
  const [view, setView] = useState<View | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);

  // Een platformbeheerder hoort bij geen bedrijf; zijn sessie kent geen organisaties.
  const tenants = useMyTenants(zone === 'admin').data ?? [];
  const { switchTo, busy } = useSwitchTenant('admin');
  // MST-10: wisselen tussen het beheer en de app van elke organisatie, en Master. Ook met één
  // organisatie: dan is het de weg naar de app, of naar Master.
  const platformAccess = Boolean(session.profile?.platformAccess);
  const canSwitch = zone === 'platform' || tenants.length > 0 || platformAccess;
  const goTo = (to: '/admin' | '/app' | '/master') => {
    setView(null);
    void navigate({ to });
  };

  const name = session.profile?.displayName ?? '—';
  const email = session.profile?.email ?? '';
  const language = i18n.language.slice(0, 2) as Language;
  const open = view !== null;

  // Dicht bij een klik ernaast, zoals elk menu.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setView(null);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  // Bij openen en bij elke wissel van lijst staat de focus op het eerste item.
  useEffect(() => {
    if (!view) return;
    menu.current?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus();
  }, [view]);

  const close = () => {
    setView(null);
    button.current?.focus();
  };

  const onMenuKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === 'Tab') {
      setView(null);
      return;
    }
    const items = Array.from(
      menu.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not(:disabled)') ?? [],
    );
    if (items.length === 0) return;
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'ArrowDown'
        ? (index + 1) % items.length
        : event.key === 'ArrowUp'
          ? (index - 1 + items.length) % items.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? items.length - 1
              : null;
    if (next === null) return;
    event.preventDefault();
    items[next]?.focus();
  };

  const onLogout = async () => {
    setView(null);
    await session.logout();
    void navigate({ to: zone === 'platform' ? '/master/login' : '/admin/login' });
  };

  const back = (
    <button type="button" role="menuitem" className="a-menu-item" onClick={() => setView('main')}>
      <AdminIcon name="arrowLeft" size={16} />
      {t('beheer.shell.back')}
    </button>
  );

  return (
    <div ref={root} className="header-actions">
      <button
        ref={button}
        type="button"
        className={cn('a-avatar-btn', open && 'open')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('beheer.shell.avatarMenu', { name })}
        data-testid="admin-avatar-button"
        onClick={() => setView(open ? null : 'main')}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setView('main');
          }
        }}
      >
        <Avatar name={name} size="md" />
        <div className="header-profile-info" style={{ textAlign: 'left' }}>
          <div className="header-profile-name">{name}</div>
          <div className="header-profile-role">{roleLine}</div>
        </div>
        <AdminIcon name="chevronDown" size={14} style={{ color: 'var(--a-muted)' }} />
      </button>

      {open ? (
        <div
          ref={menu}
          className="a-menu"
          role="menu"
          aria-label={t('beheer.shell.avatarMenu', { name })}
          data-testid="admin-avatar-menu"
          onKeyDown={onMenuKey}
        >
          <div className="a-menu-head" role="presentation">
            <div className="a-name">{name}</div>
            {email ? <div className="a-meta">{email}</div> : null}
          </div>

          {view === 'main' ? (
            <>
              {zone === 'admin' ? (
                <button
                  type="button"
                  role="menuitem"
                  className="a-menu-item"
                  onClick={() => {
                    setView(null);
                    void navigate({ to: '/admin/account' });
                  }}
                >
                  <AdminIcon name="lock" size={16} />
                  {t('beheer.shell.changePassword')}
                </button>
              ) : null}
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                className="a-menu-item"
                onClick={() => setView('language')}
              >
                <AdminIcon name="globe" size={16} />
                {t('beheer.shell.language')}
                <span className="a-menu-right">{languageLabels[language] ?? language}</span>
              </button>
              {canSwitch ? (
                <button
                  type="button"
                  role="menuitem"
                  aria-haspopup="menu"
                  className="a-menu-item"
                  disabled={busy}
                  onClick={() => setView('tenant')}
                  data-testid="admin-menu-tenant"
                >
                  <AdminIcon name="building" size={16} />
                  {t('beheer.shell.switchTo')}
                  {zone === 'admin' ? <span className="a-menu-right">{tenants.length}</span> : null}
                </button>
              ) : null}
              <div className="a-menu-sep" role="separator" />
              <button
                type="button"
                role="menuitem"
                className="a-menu-item"
                onClick={() => void onLogout()}
              >
                <AdminIcon name="logout" size={16} />
                {t('beheer.shell.logout')}
              </button>
            </>
          ) : null}

          {view === 'language' ? (
            <>
              {back}
              <div className="a-menu-sep" role="separator" />
              {supportedLanguages.map((option) => (
                <button
                  key={option}
                  type="button"
                  role="menuitemradio"
                  aria-checked={option === language}
                  lang={option}
                  className="a-menu-item"
                  onClick={() => {
                    setLanguage(option);
                    close();
                  }}
                >
                  <AdminIcon
                    name="check"
                    size={16}
                    style={{ opacity: option === language ? 1 : 0 }}
                  />
                  {languageLabels[option]}
                </button>
              ))}
            </>
          ) : null}

          {view === 'tenant' ? (
            <>
              {back}
              <div className="a-menu-sep" role="separator" />
              {zone === 'platform' ? (
                <>
                  {/* Vanuit Master: terug naar je organisaties, met hun eigen sessie (MST-10). */}
                  <button
                    type="button"
                    role="menuitem"
                    className="a-menu-item"
                    data-testid="menu-to-admin"
                    onClick={() => goTo('/admin')}
                  >
                    <AdminIcon name="building" size={16} />
                    {t('beheer.shell.toAdmin')}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="a-menu-item"
                    onClick={() => goTo('/app')}
                  >
                    <AdminIcon name="tablet" size={16} />
                    {t('beheer.shell.toApp')}
                  </button>
                </>
              ) : (
                tenants.map((tenant) => (
                  <div key={tenant.tenantCode} role="group" aria-label={tenant.name}>
                    <div className="a-menu-label" role="presentation">
                      <AdminIcon
                        name="check"
                        size={14}
                        style={{ opacity: tenant.current ? 1 : 0 }}
                      />{' '}
                      {tenant.name}
                    </div>
                    <button
                      type="button"
                      role="menuitem"
                      className="a-menu-item"
                      data-testid="admin-menu-tenant-option"
                      disabled={busy || tenant.current}
                      onClick={() => {
                        setView(null);
                        void switchTo(tenant.tenantCode, 'admin');
                      }}
                    >
                      <AdminIcon name="wrench" size={16} />
                      {t('beheer.shell.admin')}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="a-menu-item"
                      data-testid="admin-menu-tenant-app"
                      disabled={busy}
                      onClick={() => {
                        if (tenant.current) goTo('/app');
                        else {
                          setView(null);
                          void switchTo(tenant.tenantCode, 'app');
                        }
                      }}
                    >
                      <AdminIcon name="tablet" size={16} />
                      {t('beheer.shell.app')}
                    </button>
                  </div>
                ))
              )}
              {zone === 'admin' && platformAccess ? (
                <>
                  <div className="a-menu-sep" role="separator" />
                  {/* Master: een eigen aanmelding met de tweede factor; daarna blijft ze staan. */}
                  <button
                    type="button"
                    role="menuitem"
                    className="a-menu-item"
                    data-testid="menu-to-master"
                    onClick={() => goTo('/master')}
                  >
                    <AdminIcon name="shieldCheck" size={16} />
                    {t('beheer.shell.toMaster')}
                  </button>
                </>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
